import * as Sentry from '@sentry/react';
import firebase from 'firebase/compat/app';
import { useRef } from 'react';
import { CreateOrderDTO, FoodItem, listRestaurantType } from '../../src/interfaces/types';
import { auth, authReady, db } from '../../src/libs/firebase';
import db_config from '../configs/db_config';
import { useOrderContext } from '../context/OrderContext';
import { useSession } from '../context/SessionContext';
import { isRestaurantOnline } from '../utils';
import { getPreOrderQuantityStatusForCartItem, isPreOrderDisabled } from '../utils/preOrderDisabled';
import { fetchGameDayData } from '../utils/timeSlots';

const CREATE_ORDER_DEBUG_FALLBACK_LOG_ID = 'create_order_debug_fallback';

// console.* in this app only ever reaches the guest's own browser console — nobody on the
// team will ever see it. Failures worth investigating later must be written somewhere the
// team can actually query, same pattern as `writePaymentDebugLog` in Payment.tsx.
const sanitizeForLog = (value: any) => {
  if (value === undefined) return null;
  try {
    return JSON.parse(JSON.stringify(value, (_key, item) => (item === undefined ? null : item)));
  } catch (error) {
    return null;
  }
};

const writeCreateOrderDebugLog = async (orderId: string | null | undefined, data: Record<string, any>) => {
  try {
    await db
      .collection(db_config.DB_TABLE_STADIUM_LOGS)
      .doc(orderId || CREATE_ORDER_DEBUG_FALLBACK_LOG_ID)
      .set(
        {
          create_order_debug_errors: firebase.firestore.FieldValue.arrayUnion(
            sanitizeForLog({ ...data, created_at: new Date().toISOString() })
          ),
          create_order_debug_updated_at: firebase.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true }
      );
  } catch (error) {
    console.warn('[customer_create_order] failed to write debug log', error);
  }
};

const useCreateOrder = () => {
  const errorOrder = useRef(true);
  const { updateOrderDetails } = useOrderContext();
  const { session } = useSession();

  const checkOrder = async (orderDetails: CreateOrderDTO): Promise<boolean> => {
    try {
      const queryRestaurant = await db.collection(db_config.DB_TABLE_RESTAURANT_CONFIG).doc(orderDetails.restaurant_id).get();

      if (queryRestaurant.exists) {
        const restaurantData = queryRestaurant.data() as listRestaurantType;

        if (restaurantData && isRestaurantOnline(restaurantData)) {
          const queryFoods = await db.collection(db_config.DB_TABLE_ALL_FOOD_DETAILS).where('restaurantId', '==', orderDetails.restaurant_id).get();
          const queryCategories = await db.collection(db_config.DB_TABLE_ALL_CATEGORIES).where('restaurantId', '==', orderDetails.restaurant_id).get();

          const foods = queryFoods.docs.map((doc) => doc.data());
          const categories = queryCategories.docs.map((doc) => doc.data());

          for (const food of orderDetails.foods) {
            const restaurantFood = foods.find((f) => f.id === food.item_food_id);
            if (!restaurantFood || !restaurantFood.enabled) {
              sessionStorage.setItem('ErrorFood', food.item_name);
              return false;
            }
          }

          for (const food of orderDetails.foods) {
            const orderCategory = categories.find((f) => f.id === food.item_category_id);
            if (!orderCategory || orderCategory.category_show === 0) {
              sessionStorage.setItem('ErrorFood', orderCategory ? `${orderCategory.title}` : `Category`);
              return false;
            }
          }
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    } catch (error) {
      console.log('error:', error);
      return false;
    }
  };

  const checkOrderV2 = async (orderDetails: CreateOrderDTO): Promise<boolean> => {
    try {
      const orderFoods = [...(orderDetails.foods || []), ...(orderDetails.promotion_foods || [])];
      if (orderFoods.length === 0) return false;

      const restaurantIds = Array.from(new Set(orderFoods.map((f: any) => f.restaurant_id || f.restaurantId).filter(Boolean))) as string[];
      const foodIds = Array.from(new Set(orderFoods.map((f: any) => f.item_food_id).filter(Boolean))) as string[];
      const categoryIds = Array.from(new Set(orderFoods.map((f: any) => f.item_category_id).filter(Boolean))) as string[];

      if (restaurantIds.length === 0 || foodIds.length === 0) return false;

      const [restaurantDocs, foodDocs, categoryDocs] = await Promise.all([
        Promise.all(restaurantIds.map((id) => db.collection(db_config.DB_TABLE_RESTAURANT_CONFIG).doc(id).get())),
        Promise.all(foodIds.map((id) => db.collection(db_config.DB_TABLE_ALL_FOOD_DETAILS).doc(id).get())),
        Promise.all(categoryIds.map((id) => db.collection(db_config.DB_TABLE_USER_MENU_CATEGORIES).doc(id).get())),
      ]);

      const restaurantMap = new Map<string, listRestaurantType>();
      for (const doc of restaurantDocs) {
        if (!doc.exists) return false;
        const data = doc.data() as listRestaurantType;
        if (!data || !isRestaurantOnline(data)) return false;
        restaurantMap.set(doc.id, data);
      }

      const foodMap = new Map<string, any>();
      for (const doc of foodDocs) {
        if (doc.exists) foodMap.set(doc.id, doc.data());
      }

      const getMasterFoodId = (foodId: string) => {
        const foodDetail = foodMap.get(foodId);
        return foodDetail?.food_id || foodId;
      };

      const categoryMap = new Map<string, any>();
      for (const doc of categoryDocs) {
        if (doc.exists) categoryMap.set(doc.id, doc.data());
      }

      const isPreOrder =
        orderDetails.is_pre_order === true ||
        String(orderDetails.order_type || '').includes('pre_order');
      const requestedDeliveryDate = (orderDetails.requested_delivery_time as any)?.toDate
        ? (orderDetails.requested_delivery_time as any).toDate()
        : undefined;
      const orderDate = requestedDeliveryDate ? requestedDeliveryDate.toISOString() : undefined;
      const gameDayData =
        isPreOrder && orderDetails.stadium_id && orderDate
          ? await fetchGameDayData(orderDetails.stadium_id, orderDate)
          : null;

      for (const food of orderFoods) {
        const restaurantFood = foodMap.get(food.item_food_id);
        if (!restaurantFood) {
          sessionStorage.setItem('ErrorFood', food.item_name);
          return false;
        }

        if (isPreOrder) {
          const restaurantId = (food as any).restaurant_id || food.restaurantId || restaurantFood.restaurantId;
          const restaurant = restaurantMap.get(restaurantId);
          const preOrderStatus = isPreOrderDisabled(
            { id: food.item_food_id, ...restaurantFood },
            restaurant,
            orderDate,
            true,
            orderDetails.delivery_request,
            gameDayData
          );

          if (preOrderStatus.disabled) {
            sessionStorage.setItem('ErrorFood', food.item_name);
            return false;
          }

          const quantityStatus = getPreOrderQuantityStatusForCartItem({
            food: restaurantFood,
            orderDate,
            cartFoods: orderFoods,
            itemFoodId: food.item_food_id,
            getMasterFoodId,
          });
          if (quantityStatus.exceedsLimit) {
            sessionStorage.setItem('ErrorFood', food.item_name);
            return false;
          }
        } else if (!restaurantFood.enabled) {
          sessionStorage.setItem('ErrorFood', food.item_name);
          return false;
        }

        const orderCategory = categoryMap.get(food.item_category_id);
        if (!orderCategory || orderCategory.category_show === 0) {
          sessionStorage.setItem('ErrorFood', orderCategory ? `${orderCategory.title}` : `Category`);
          return false;
        }
      }

      return true;
    } catch (error) {
      console.log('error:', error);
      return false;
    }
  };

  const checkOrderId = async (orderId: string): Promise<boolean> => {
    try {
      const queryOrder = await db.collection(db_config.DB_TABLE_ALL_ORDERS).doc(orderId).get();
      return queryOrder.exists;
    } catch (error) {
      return true;
    }
  };

  const getAvailableOrderId = async (baseOrderId: string): Promise<string> => {
    let attempt = 0;
    let newOrderId = baseOrderId;

    while (await checkOrderId(newOrderId)) {
      attempt += 1;
      newOrderId = `${baseOrderId}-${attempt}`;
    }

    return newOrderId;
  };

  const createOrder = async (
    orderDetails: CreateOrderDTO,
    orderId: string,
    include_session_id: boolean = true
  ): Promise<boolean> => {
    try {
      const isValidOrder = include_session_id ? await checkOrder(orderDetails) : await checkOrderV2(orderDetails);
      errorOrder.current = isValidOrder;

      if (!isValidOrder) {
        await writeCreateOrderDebugLog(orderId, {
          reason: 'validation_rejected',
          restaurantId: orderDetails.restaurant_id,
          errorFood: sessionStorage.getItem('ErrorFood'),
        });
        Sentry.captureMessage('create_order_validation_rejected', {
          level: 'warning',
          tags: { order_id: orderId || undefined },
          extra: { restaurantId: orderDetails.restaurant_id, errorFood: sessionStorage.getItem('ErrorFood') },
        });
        return false;
      }

      const finalOrderId = await getAvailableOrderId(orderId);
      updateOrderDetails({ ...orderDetails, order_id: finalOrderId });
      sessionStorage.setItem('orderId', finalOrderId);

      let uuid: string | null = null;
      let retries = 3;
      while (retries > 0) {
        uuid = localStorage.getItem('uuid');
        if (uuid) break;
        retries--;
      }

      const cleanFoods = (foods: FoodItem[]) => {
        if (!Array.isArray(foods)) return foods;
        return foods.map((f: any) => {
          const { restaurant_name, restaurant_profile_pic, ...rest } = f;
          return rest;
        });
      };

      await authReady;

      const payload: any = {
        ...orderDetails,
        foods: cleanFoods(orderDetails.foods),
        promotion_foods: cleanFoods(orderDetails.promotion_foods),
        order_id: finalOrderId,
        ordered_by: uuid,
        customer_uid: auth.currentUser?.uid || null,
        updated_by: auth.currentUser?.uid || null,
        order_create_time: firebase.firestore.FieldValue.serverTimestamp(),
      };

      if (include_session_id) {
        payload.session_id = session?.sessionId;
      }

      const orderRef = db.collection(db_config.DB_TABLE_ALL_ORDERS).doc(finalOrderId);
      await orderRef.set(payload);

      const createdOrder = await orderRef.get({ source: 'server' });
      if (!createdOrder.exists) {
        await writeCreateOrderDebugLog(finalOrderId, { reason: 'write_did_not_persist' });
        Sentry.captureMessage('create_order_write_did_not_persist', {
          level: 'error',
          tags: { order_id: finalOrderId },
        });
        return false;
      }

      return true;
    } catch (error) {
      await writeCreateOrderDebugLog(orderId, {
        reason: 'exception',
        restaurantId: orderDetails.restaurant_id,
        error_message: error instanceof Error ? error.message : String(error),
      });
      Sentry.captureException(error, {
        tags: { order_id: orderId || undefined },
        extra: { restaurantId: orderDetails.restaurant_id },
      });
      return false;
    }
  };

  return {
    createOrder,
    errorOrder,
    checkOrder,
    checkOrderV2,
  };
};

export default useCreateOrder;
